/**
 * Block settings — schedule, grading and visibility in one dialog.
 *
 * Which controls appear is decided by the block itself, not by a category switch here.
 * Only a subsection carries a due date and a grader type; only a block whose ancestors
 * are unlocked can set its own staff lock; hiding from the table of contents exists only
 * where the platform has enabled the UI for it. Rendering a field the platform will
 * ignore is worse than omitting it — the author sets it, sees it save, and it does
 * nothing.
 */

import { useState } from 'react';
import { Banner, Button, Dialog, Field, SelectField } from '../../shared/ui';
import type { BlockSettings, OutlineBlock } from '../../shared/lib/studio';
import { isoToLocalInput, isUnscheduled, localInputToIso } from './dates';

/** Sentinel for "not graded". An empty `<option>` value is indistinguishable from unset. */
const UNGRADED = '__ungraded__';

export function ConfigureDialog({
  block,
  childLabel,
  onClose,
  onSave,
  busy,
  error,
}: {
  block: OutlineBlock;
  /** What this block is called, from the parent's `child_info.display_name`. */
  childLabel: string;
  onClose: () => void;
  onSave: (settings: BlockSettings) => void;
  busy: boolean;
  error: unknown;
}) {
  const scheduled = !isUnscheduled(block.start);
  const [start, setStart] = useState(scheduled ? isoToLocalInput(block.start) : '');
  const [due, setDue] = useState(isoToLocalInput(block.due));
  const [grader, setGrader] = useState(block.format ?? UNGRADED);
  const [staffOnly, setStaffOnly] = useState(block.has_explicit_staff_lock);
  const [hidden, setHidden] = useState(block.hide_from_toc);

  // A due date is only meaningful where work is submitted, which in the outline is the
  // subsection — the same place grading is configured.
  const gradable = block.category === 'sequential';

  function submit() {
    const graded = grader !== UNGRADED;
    onSave({
      start: localInputToIso(start),
      visible_to_staff_only: staffOnly ? true : null,
      ...(block.enable_hide_from_toc_ui ? { hide_from_toc: hidden } : {}),
      ...(gradable ? { due: localInputToIso(due), graded, format: graded ? grader : null } : {}),
    });
  }

  return (
    <Dialog
      title={`${childLabel} settings`}
      onClose={onClose}
      footer={
        <>
          <Button variant="primary" busy={busy} onClick={submit}>
            Save settings
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <p className="outline-dialog__subject">{block.display_name}</p>

      <Field
        label="Release date"
        name="start"
        type="datetime-local"
        value={start}
        onChange={(e) => setStart(e.target.value)}
        hint="UTC. Leave empty to keep it unscheduled — learners cannot see unscheduled content."
      />

      {gradable && (
        <>
          <Field
            label="Due date"
            name="due"
            type="datetime-local"
            value={due}
            onChange={(e) => setDue(e.target.value)}
            hint="UTC. Only meaningful once an assignment type is set."
          />
          <SelectField
            label="Assignment type"
            name="format"
            value={grader}
            onChange={(e) => setGrader(e.target.value)}
            options={[
              [UNGRADED, 'Not graded'],
              ...block.course_graders.map((g) => [g, g] as [string, string]),
            ]}
          />
        </>
      )}

      <fieldset className="outline-dialog__toggles">
        <legend>Visibility</legend>

        {block.ancestor_has_staff_lock ? (
          // The lock is inherited, so a checkbox here would look like it could be turned
          // off — it cannot, and the fix is one level up.
          <Banner tone="info">
            Hidden from learners because a parent is. Unlock the parent to change this.
          </Banner>
        ) : (
          <label className="outline-dialog__check">
            <input
              type="checkbox"
              checked={staffOnly}
              onChange={(e) => setStaffOnly(e.target.checked)}
            />
            <span>
              <strong>Hide from learners</strong>
              <small>Staff can still see it. Applies to everything inside.</small>
            </span>
          </label>
        )}

        {block.enable_hide_from_toc_ui && (
          <label className="outline-dialog__check">
            <input type="checkbox" checked={hidden} onChange={(e) => setHidden(e.target.checked)} />
            <span>
              <strong>Hide from the course outline</strong>
              <small>Learners can still reach it from a direct link.</small>
            </span>
          </label>
        )}
      </fieldset>

      {error != null && (
        <Banner tone="danger">
          <strong>These settings were not saved.</strong> Studio rejected the change, so the
          block is unchanged. Check the dates and try again.
        </Banner>
      )}
    </Dialog>
  );
}
